import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { ComputeUsagePanel } from './components/ComputeUsagePanel';
import { EnterprisePanel } from './components/EnterprisePanel';
import { TrialsPanel } from './components/TrialsPanel';
import { EmailsPanel } from './components/EmailsPanel';
import { HiddenCompaniesPanel } from './components/HiddenCompaniesPanel';
import { IS_RECMAPPER } from './product';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  created_at: string;
  last_login_at: string | null;
  last_activity_at: string | null;
  activity_count: number;
  project_count: number;
  subscription_status: string | null;
  stripe_customer_id: string | null;
  team_id: number | null;
  team_role: string | null;
  team_name: string | null;
  team_owner_id: number | null;
  team_member_count: number;
  logo_url: string | null;
  team_logo_url: string | null;
  ada_enabled: boolean;
  universe_access?: boolean;
  universe_access_via_team?: boolean;
  monitoring_plan_id: string | null;
  monitoring_status: string | null;
  tier_name: string | null;
  billing_cycle: string | null;
}

// Render a unified subscription badge from the two independent statuses
// (users.subscription_status = Pro; user_monitoring_subscriptions.status = Monitoring).
// Monitoring supersedes Pro — when someone upgrades to monitoring we mark Pro
// as canceled in the DB, but they are still an active paying customer via
// the monitoring table, so we should show "Monitoring" not "Cancelled".
function renderSubscriptionBadge(
  subscriptionStatus: string | null,
  monitoringStatus: string | null,
  monitoringPlanId: string | null,
  tierName?: string | null
): { label: string; className: string } {
  // New tier system (Starter/Growth/Scale) supersedes everything else.
  // A 7-day trial gets its own loud badge so trialists are easy to watch.
  if (tierName) {
    if (monitoringStatus === 'trialing') {
      return { label: `${tierName} — 7-day trial`, className: 'bg-blue-100 text-blue-800 font-semibold' };
    }
    if (monitoringStatus === 'active') {
      return { label: tierName, className: 'bg-green-100 text-green-800' };
    }
    if (monitoringStatus === 'past_due') {
      return { label: `${tierName} — past due`, className: 'bg-yellow-100 text-yellow-800' };
    }
    if (monitoringStatus === 'cancelled' || monitoringStatus === 'canceled' || monitoringStatus === 'unpaid') {
      return { label: `Cancelled (${tierName})`, className: 'bg-gray-100 text-gray-600' };
    }
  }
  const proActive = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
  const proCancelled = subscriptionStatus === 'canceled' || subscriptionStatus === 'cancelled';
  const proPastDue = subscriptionStatus === 'past_due';
  const monitoringActive = monitoringStatus === 'active' || monitoringStatus === 'trialing';
  const monitoringCancelled = monitoringStatus === 'cancelled' || monitoringStatus === 'canceled';
  const monitoringPastDue = monitoringStatus === 'past_due';

  // Active monitoring supersedes everything
  if (monitoringActive) {
    const planLabel = monitoringPlanId
      ? `Monitoring — ${monitoringPlanId.charAt(0).toUpperCase() + monitoringPlanId.slice(1).replace(/_/g, ' ')}`
      : 'Monitoring';
    return { label: planLabel, className: 'bg-green-100 text-green-800' };
  }
  // Past due on monitoring is a billing problem but still technically active
  if (monitoringPastDue) {
    return { label: 'Monitoring — past due', className: 'bg-yellow-100 text-yellow-800' };
  }
  // Fall through to Pro status
  if (proActive) {
    return { label: 'Pro', className: 'bg-green-100 text-green-800' };
  }
  if (proPastDue) {
    return { label: 'Pro — past due', className: 'bg-yellow-100 text-yellow-800' };
  }
  // Cancelled states — show which one they were last on
  if (monitoringCancelled) {
    return { label: 'Cancelled (Monitoring)', className: 'bg-gray-100 text-gray-600' };
  }
  if (proCancelled) {
    return { label: 'Cancelled (Pro)', className: 'bg-gray-100 text-gray-600' };
  }
  return { label: 'None', className: 'bg-gray-100 text-gray-500' };
}

interface UserDetails {
  user: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    company: string | null;
    email_verified: boolean;
    created_at: string;
    last_login_at: string | null;
    subscription_status: string | null;
    monitoring_plan_id: string | null;
    monitoring_status: string | null;
    tier_name: string | null;
    billing_cycle: string | null;
    stripe_customer_id: string | null;
  };
  projects: Array<{
    id: number;
    name: string;
    description: string;
    created_at: string;
  }>;
}

interface TransferModalState {
  isOpen: boolean;
  projectId: number | null;
  projectName: string;
  fromUserId: number | null;
}

interface Team {
  id: number;
  name: string;
  logo_url: string | null;
  created_at: string;
  member_count: number;
}

interface MonitoringStats {
  today: {
    checksPerformed: number;
    changesDetected: number;
    jobChanges: number;
    activeUsers: number;
  };
  thisWeek: {
    checksPerformed: number;
    changesDetected: number;
    jobChanges: number;
    activeUsers: number;
  };
  thisMonth: {
    checksPerformed: number;
    changesDetected: number;
    jobChanges: number;
    activeUsers: number;
  };
  subscriptions: {
    active: number;
    cancelled: number;
    pastDue: number;
    total: number;
    breakdown: Array<{
      planId: string;
      count: number;
      totalAllowance: number;
      totalUsed: number;
    }>;
  };
  subscribers: Array<{
    userId: number;
    email: string;
    firstName: string;
    lastName: string;
    planId: string;
    monthlyAllowance: number;
    checksUsedThisMonth: number;
    candidatesMonitored: number;
    status: string;
  }>;
  totalMonthlyAllowance: number;
  estimatedMonthlyRevenue: number;
}

export const AdminDashboard: React.FC = () => {
  const { user: authUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // ADD THESE TWO LINES HERE:
  const [transferModal, setTransferModal] = useState<TransferModalState>({
    isOpen: false,
    projectId: null,
    projectName: '',
    fromUserId: null
  });
  const [targetUserId, setTargetUserId] = useState<string>('');
  const [transferUserSearch, setTransferUserSearch] = useState<string>('');

  // Team logo management state
  const [teams, setTeams] = useState<Team[]>([]);
  const [editingTeamId, setEditingTeamId] = useState<number | null>(null);
  const [editingLogoUrl, setEditingLogoUrl] = useState<string>('');

  // User logo management state (for solo users)
  const [editingUserLogoId, setEditingUserLogoId] = useState<number | null>(null);
  const [editingUserLogoUrl, setEditingUserLogoUrl] = useState<string>('');

  // User delete state
  const [deleteUserConfirm, setDeleteUserConfirm] = useState<{ show: boolean; userId: number | null; userEmail: string }>({
    show: false,
    userId: null,
    userEmail: ''
  });

  // Credit addition state
  const [addCreditsModal, setAddCreditsModal] = useState<{
    show: boolean;
    userId: number | null;
    userName: string;
    userEmail: string;
  }>({
    show: false,
    userId: null,
    userName: '',
    userEmail: ''
  });
  const [creditsToAdd, setCreditsToAdd] = useState<string>('');
  const [creditReason, setCreditReason] = useState<string>('');
  const [isAddingCredits, setIsAddingCredits] = useState(false);

  // Gift bolt-on state (gives the user extra monitoring or jobs quota
  // without charging Stripe — uses /api/subscription/admin/users/:userId/
  // bolt-ons which inserts a row with stripe_subscription_item_id NULL).
  const [giftBoltOnModal, setGiftBoltOnModal] = useState<{
    show: boolean;
    userId: number | null;
    userName: string;
    userEmail: string;
  }>({
    show: false,
    userId: null,
    userName: '',
    userEmail: '',
  });
  const [boltOnType, setBoltOnType] = useState<'monitoring' | 'jobs'>('monitoring');
  const [boltOnQuantity, setBoltOnQuantity] = useState<string>('1');
  const [isGiftingBoltOn, setIsGiftingBoltOn] = useState(false);

  // Monitoring stats state
  const [monitoringStats, setMonitoringStats] = useState<MonitoringStats | null>(null);

  // Admin notifications state
  const [adminNotifications, setAdminNotifications] = useState<Array<{
    id: number;
    notification_type: string;
    priority: string;
    title: string;
    message: string;
    metadata: any;
    user_id: number;
    user_email: string;
    is_read: boolean;
    is_resolved: boolean;
    created_at: string;
  }>>([]);
  const [notificationCount, setNotificationCount] = useState(0);

  // Team expansion state for collapsible team groups
  const [expandedTeams, setExpandedTeams] = useState<Set<number>>(new Set());

  const toggleTeamExpand = (teamId: number) => {
    setExpandedTeams(prev => {
      const newSet = new Set(prev);
      if (newSet.has(teamId)) {
        newSet.delete(teamId);
      } else {
        newSet.add(teamId);
      }
      return newSet;
    });
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    fetchUsers();
    fetchTeams();
    fetchMonitoringStats();
    fetchAdminNotifications();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/users', {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId: number) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user details');
      }

      const data = await response.json();
      setSelectedUser(data);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await fetch('/api/admin/teams', {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch teams');
      }

      const data = await response.json();
      setTeams(data);
    } catch (err: any) {
      console.error('Error fetching teams:', err);
      // Don't show error for teams - it's optional
    }
  };

  const fetchMonitoringStats = async () => {
    try {
      const response = await fetch('/api/admin/monitoring/stats', {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        console.error('Failed to fetch monitoring stats');
        return;
      }

      const data = await response.json();
      setMonitoringStats(data);
    } catch (err: any) {
      console.error('Error fetching monitoring stats:', err);
      // Don't show error - monitoring tables might not exist yet
    }
  };

  const fetchAdminNotifications = async () => {
    try {
      const response = await fetch('/api/admin/notifications?unreadOnly=true&limit=20', {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        console.error('Failed to fetch admin notifications');
        return;
      }

      const data = await response.json();
      setAdminNotifications(data.notifications || []);
      setNotificationCount(data.unreadCount || 0);
    } catch (err: any) {
      console.error('Error fetching admin notifications:', err);
      // Don't show error - table might not exist yet
    }
  };

  const resolveNotification = async (notificationId: number) => {
    try {
      const response = await fetch(`/api/admin/notifications/${notificationId}/resolve`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        fetchAdminNotifications();
      }
    } catch (err) {
      console.error('Error resolving notification:', err);
    }
  };

  const handleUpdateTeamLogo = async (teamId: number, logoUrl: string) => {
    try {
      const response = await fetch(`/api/admin/teams/${teamId}/logo`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ logoUrl: logoUrl.trim() || null })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update team logo');
      }

      await fetchTeams();
      setEditingTeamId(null);
      setEditingLogoUrl('');
      alert('Team logo updated successfully');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdateUserLogo = async (userId: number, logoUrl: string) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/logo`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ logoUrl: logoUrl.trim() || null })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update user logo');
      }

      await fetchUsers();
      setEditingUserLogoId(null);
      setEditingUserLogoUrl('');
      alert('User logo updated successfully');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserConfirm.userId) return;

    try {
      const response = await fetch(`/api/admin/users/${deleteUserConfirm.userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user');
      }

      await fetchUsers();
      setDeleteUserConfirm({ show: false, userId: null, userEmail: '' });
      alert('User deleted successfully');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAddCredits = async () => {
    if (!addCreditsModal.userId || !creditsToAdd) return;

    const credits = parseInt(creditsToAdd);
    if (isNaN(credits) || credits === 0) {
      alert('Please enter a valid credit amount (positive to add, negative to remove)');
      return;
    }

    setIsAddingCredits(true);
    try {
      const response = await fetch(`/api/admin/users/${addCreditsModal.userId}/add-credits`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          credits: credits,
          reason: creditReason || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add credits');
      }

      const data = await response.json();
      alert(data.message);

      // Reset and close modal
      setAddCreditsModal({ show: false, userId: null, userName: '', userEmail: '' });
      setCreditsToAdd('');
      setCreditReason('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsAddingCredits(false);
    }
  };

  const handleGiftBoltOn = async () => {
    if (!giftBoltOnModal.userId) return;
    const qty = parseInt(boltOnQuantity, 10);
    if (isNaN(qty) || qty < 1) {
      window.alert('Quantity must be 1 or more.');
      return;
    }
    const perUnit = boltOnType === 'monitoring' ? 2500 : 500;
    const total = qty * perUnit;
    const label = boltOnType === 'monitoring' ? 'monitoring checks/mo' : 'tracked jobs/mo';
    if (!window.confirm(
      `Gift ${qty} ${boltOnType} bolt-on${qty > 1 ? 's' : ''} ` +
      `(${total.toLocaleString()} ${label}) to ${giftBoltOnModal.userName}?\n\n` +
      `No Stripe charge — added directly to their entitlements.`
    )) return;

    setIsGiftingBoltOn(true);
    try {
      const response = await fetch(
        `/api/subscription/admin/users/${giftBoltOnModal.userId}/bolt-ons`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ type: boltOnType, quantity: qty }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to gift bolt-on');
      }
      window.alert(
        `Gifted ${qty} ${boltOnType} bolt-on${qty > 1 ? 's' : ''} ` +
        `(${total.toLocaleString()} ${label}).`
      );
      setGiftBoltOnModal({ show: false, userId: null, userName: '', userEmail: '' });
      setBoltOnQuantity('1');
      setBoltOnType('monitoring');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsGiftingBoltOn(false);
    }
  };

const handleToggleSearchAccess = async (userId: number, currentlyEnabled: boolean) => {
  const next = !currentlyEnabled;
  if (!window.confirm(`${next ? 'Give' : 'Remove'} Search access (the 400M+ profile universe) for this user?`)) {
    return;
  }
  try {
    const response = await fetch(`/api/admin/users/${userId}/search-access`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: next })
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || `Failed to ${next ? 'enable' : 'disable'} Search access`);
    }
    await fetchUsers();
  } catch (err: any) {
    console.error('Search access toggle error:', err);
    setError(err.message);
  }
};

const handleToggleAdaAccess = async (userId: number, currentlyEnabled: boolean) => {
  const next = !currentlyEnabled;
  const verb = next ? 'enable' : 'disable';
  if (!window.confirm(`${next ? 'Enable' : 'Disable'} Ada (AI agent) access for this user?`)) {
    return;
  }

  try {
    const response = await fetch(`/api/admin/users/${userId}/ada-access`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: next })
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || `Failed to ${verb} Ada access`);
    }

    // Refresh users list so the toggle UI reflects the new state
    await fetchUsers();
  } catch (err: any) {
    console.error('Ada access toggle error:', err);
    setError(err.message);
  }
};

const handleImpersonate = async (userId: number) => {
  if (!window.confirm('Are you sure you want to impersonate this user? You will be logged in as them.')) {
    return;
  }

  try {
    console.log('Starting impersonation for user:', userId);
    
    const response = await fetch(`/api/admin/impersonate/${userId}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });

    console.log('Impersonation response status:', response.status);

    if (!response.ok) {
      throw new Error('Failed to impersonate user');
    }

    const data = await response.json();
    console.log('Impersonation data received:', data);
    
    // Store admin identity before replacing token (for features that persist across impersonation)
    if (authUser?.email) {
      localStorage.setItem('adminEmail', authUser.email);
    }

    // Store the new token
    localStorage.setItem('token', data.token);
    console.log('Token stored, current token:', localStorage.getItem('token'));
    
    // Force a full page reload
    console.log('Redirecting to /');
    window.location.href = '/';
    
  } catch (err: any) {
    console.error('Impersonation error:', err);
    setError(err.message);
  }
};

const [resetPasswordResult, setResetPasswordResult] = useState<{ email: string; password: string } | null>(null);

const handleResetPassword = async (userId: number, userEmail: string) => {
  if (!window.confirm(`Reset password for ${userEmail}? A new temporary password will be generated.`)) {
    return;
  }

  try {
    const response = await fetch(`/api/admin/users/${userId}/reset-password`, {
      method: 'POST',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to reset password');
    }

    const data = await response.json();
    setResetPasswordResult({
      email: data.user.email,
      password: data.temporaryPassword
    });
  } catch (err: any) {
    console.error('Password reset error:', err);
    setError(err.message);
  }
};

  const handleUpdateRole = async (userId: number, newRole: string) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ role: newRole })
      });

      if (!response.ok) {
        throw new Error('Failed to update role');
      }

      // Refresh users list
      fetchUsers();
      if (selectedUser && selectedUser.user.id === userId) {
        fetchUserDetails(userId);
      }
      
      alert('Role updated successfully');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleTransferProject = async () => {
    if (!transferModal.projectId || !transferModal.fromUserId || !targetUserId) {
      alert('Please select a target user');
      return;
    }

    const targetUserIdNum = parseInt(targetUserId);
    
    if (targetUserIdNum === transferModal.fromUserId) {
      alert('Cannot transfer to the same user');
      return;
    }

    if (!window.confirm(`Are you sure you want to transfer "${transferModal.projectName}" to the selected user?`)) {
      return;
    }

    try {
      const response = await fetch('/api/admin/transfer-project', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          projectId: transferModal.projectId,
          fromUserId: transferModal.fromUserId,
          toUserId: targetUserIdNum
        })
      });

      if (!response.ok) {
        throw new Error('Failed to transfer project');
      }

      const data = await response.json();
      alert(data.message);
      
      // Refresh user details
      if (selectedUser) {
        fetchUserDetails(selectedUser.user.id);
      }
      fetchUsers();
      
      // Close modal
      setTransferModal({ isOpen: false, projectId: null, projectName: '', fromUserId: null });
      setTargetUserId('');
      setTransferUserSearch('');

    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDuplicateProject = async (projectId: number, projectName: string, fromUserId: number) => {
    if (!window.confirm(`Duplicate "${projectName}" to your account? This will copy all candidates, companies, and hiring managers.`)) {
      return;
    }

    try {
      const response = await fetch('/api/admin/duplicate-project', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ projectId, fromUserId })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to duplicate project');
      }

      const data = await response.json();
      alert(data.message);

    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeactivateMonitoring = async (userId: number, email: string) => {
    if (!window.confirm(`Deactivate monitoring for ${email}? This will disable monitoring on all their projects.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/monitoring/subscriptions/${userId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: 'cancelled' })
      });

      if (!response.ok) {
        throw new Error('Failed to deactivate monitoring');
      }

      alert(`Monitoring deactivated for ${email}`);
      fetchMonitoringStats();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleActivateMonitoring = async (userId: number, email: string) => {
    if (!window.confirm(`Re-activate monitoring for ${email}?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/monitoring/subscriptions/${userId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: 'active' })
      });

      if (!response.ok) {
        throw new Error('Failed to activate monitoring');
      }

      alert(`Monitoring re-activated for ${email}`);
      fetchMonitoringStats();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const matchesSearch = (user: User) =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.team_name && user.team_name.toLowerCase().includes(searchTerm.toLowerCase()));
  // A matching team owner brings their whole team — otherwise searching
  // "elliot" showed only the members whose own name contained "elliot".
  const matchedOwnerTeams = new Set(
    users.filter(u => u.team_id && u.id === u.team_owner_id && matchesSearch(u)).map(u => u.team_id)
  );
  const filteredUsers = users.filter(user =>
    matchesSearch(user) || (user.team_id != null && matchedOwnerTeams.has(user.team_id))
  );

  // Organize users: solo users first, then team owners with their members
  const organizedUsers = React.useMemo(() => {
    const soloUsers = filteredUsers.filter(u => !u.team_id);
    const teamUsers = filteredUsers.filter(u => u.team_id);

    // Group team users by team
    const teamGroups: { [key: number]: { owner: User | null; members: User[] } } = {};

    teamUsers.forEach(user => {
      if (!user.team_id) return;

      if (!teamGroups[user.team_id]) {
        teamGroups[user.team_id] = { owner: null, members: [] };
      }

      if (user.id === user.team_owner_id) {
        teamGroups[user.team_id].owner = user;
      } else {
        teamGroups[user.team_id].members.push(user);
      }
    });

    return { soloUsers, teamGroups };
  }, [filteredUsers]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-1">Manage users and access</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                Admin Mode
              </span>
              <button
                onClick={() => window.location.href = '/admin/command-center'}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
              >
                Command Center
              </button>
              <button
                onClick={() => window.location.href = '/admin/crm'}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm font-medium"
              >
                CRM
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Back to App
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-red-600 text-sm underline mt-2"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Admin Notifications - Important Alerts */}
        {adminNotifications.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-yellow-800 flex items-center gap-2">
                🔔 Notifications ({notificationCount} unresolved)
              </h3>
              <button
                onClick={fetchAdminNotifications}
                className="text-sm text-yellow-700 hover:text-yellow-900"
              >
                Refresh
              </button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {adminNotifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`p-3 rounded-lg flex items-start justify-between gap-4 ${
                    notif.priority === 'critical' ? 'bg-red-100 border-l-4 border-red-500' :
                    notif.priority === 'high' ? 'bg-orange-100 border-l-4 border-orange-500' :
                    'bg-white border-l-4 border-yellow-400'
                  }`}
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{notif.title}</p>
                    <p className="text-sm text-gray-700 mt-1">{notif.message}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(notif.created_at).toLocaleString()}
                    </p>
                  </div>
                  <button
                    onClick={() => resolveNotification(notif.id)}
                    className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 whitespace-nowrap"
                  >
                    ✓ Resolve
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <ComputeUsagePanel />

        <EnterprisePanel />

        <TrialsPanel />
        {!IS_RECMAPPER && <EmailsPanel />}
        {!IS_RECMAPPER && <HiddenCompaniesPanel />}

        {/* Monitoring Stats Section - At Top for Visibility */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6 border-b">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold">Monitoring Stats</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Capacity planning and usage overview
                </p>
              </div>
              <button
                onClick={fetchMonitoringStats}
                className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
              >
                Refresh
              </button>
            </div>
          </div>

          <div className="p-6">
            {monitoringStats ? (
              <div className="space-y-6">
                {/* Key Metrics Row */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-red-50 rounded-lg border-2 border-red-200">
                    <p className="text-3xl font-bold text-red-700">{monitoringStats.totalMonthlyAllowance.toLocaleString()}</p>
                    <p className="text-xs text-red-600 font-medium">Total Monthly Checks</p>
                    <p className="text-xs text-gray-500">Monthly plan allowance</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-700">{monitoringStats.today.checksPerformed}</p>
                    <p className="text-xs text-gray-600">Today</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-700">{monitoringStats.thisWeek.checksPerformed}</p>
                    <p className="text-xs text-gray-600">This Week</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-700">{monitoringStats.thisMonth.checksPerformed}</p>
                    <p className="text-xs text-gray-600">This Month</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-700">£{monitoringStats.estimatedMonthlyRevenue.toLocaleString()}</p>
                    <p className="text-xs text-gray-600">Monthly Revenue</p>
                  </div>
                </div>

                {/* Subscribers Table */}
                {monitoringStats.subscribers && monitoringStats.subscribers.length > 0 && (
                  <div className="border-t pt-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      Active Subscribers ({monitoringStats.subscribers.length})
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">User</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Plan</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Monthly Allowance</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Used</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Candidates</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Status</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {monitoringStats.subscribers.map((sub) => (
                            <tr key={sub.userId} className="hover:bg-gray-50">
                              <td className="px-3 py-2">
                                <div className="font-medium text-gray-900">{sub.firstName} {sub.lastName}</div>
                                <div className="text-xs text-gray-500">{sub.email}</div>
                              </td>
                              <td className="px-3 py-2">
                                <span className="capitalize font-medium">{(sub.planId || 'unknown').replace('_', ' ')}</span>
                              </td>
                              <td className="px-3 py-2 text-right font-mono">
                                {sub.monthlyAllowance.toLocaleString()}
                              </td>
                              <td className="px-3 py-2 text-right font-mono">
                                <span className={sub.checksUsedThisMonth > sub.monthlyAllowance * 0.8 ? 'text-red-600 font-bold' : ''}>
                                  {sub.checksUsedThisMonth.toLocaleString()}
                                </span>
                                <span className="text-gray-400 text-xs ml-1">
                                  ({Math.round((sub.checksUsedThisMonth / sub.monthlyAllowance) * 100)}%)
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right font-mono">
                                {sub.candidatesMonitored}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                  sub.status === 'active' ? 'bg-green-100 text-green-800' :
                                  sub.status === 'past_due' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {sub.status}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                {sub.status === 'active' ? (
                                  <button
                                    onClick={() => handleDeactivateMonitoring(sub.userId, sub.email)}
                                    className="px-2 py-1 text-xs bg-red-100 text-red-700 hover:bg-red-200 rounded"
                                  >
                                    Deactivate
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleActivateMonitoring(sub.userId, sub.email)}
                                    className="px-2 py-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 rounded"
                                  >
                                    Activate
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-100 font-medium">
                          <tr>
                            <td colSpan={2} className="px-3 py-2 text-right">Totals:</td>
                            <td className="px-3 py-2 text-right font-mono">
                              {monitoringStats.subscribers.reduce((sum, s) => sum + s.monthlyAllowance, 0).toLocaleString()}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">
                              {monitoringStats.subscribers.reduce((sum, s) => sum + s.checksUsedThisMonth, 0).toLocaleString()}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">
                              {monitoringStats.subscribers.reduce((sum, s) => sum + s.candidatesMonitored, 0)}
                            </td>
                            <td></td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {/* Subscription Summary */}
                <div className="border-t pt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-green-50 rounded">
                    <p className="text-xl font-bold text-green-600">{monitoringStats.subscriptions.active}</p>
                    <p className="text-xs text-gray-600">Active Subs</p>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded">
                    <p className="text-xl font-bold text-yellow-600">{monitoringStats.subscriptions.pastDue}</p>
                    <p className="text-xs text-gray-600">Past Due</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded">
                    <p className="text-xl font-bold text-red-600">{monitoringStats.subscriptions.cancelled}</p>
                    <p className="text-xs text-gray-600">Cancelled</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded">
                    <p className="text-xl font-bold text-blue-600">
                      {monitoringStats.thisMonth.changesDetected}
                    </p>
                    <p className="text-xs text-gray-600">Changes Detected</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                No monitoring data available yet. Stats will appear once monitoring is active.
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Users List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">All Users ({users.length})</h2>
                  <button
                    onClick={fetchUsers}
                    className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    Refresh
                  </button>
                </div>
                
                <input
                  type="text"
                  placeholder="Search users by email or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscription</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Projects</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activity</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Active</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {/* Render team groups first */}
                    {Object.entries(organizedUsers.teamGroups).map(([teamIdStr, group]) => {
                      const teamId = parseInt(teamIdStr);
                      const isExpanded = expandedTeams.has(teamId);
                      const owner = group.owner;
                      const members = group.members;

                      if (!owner) return null;

                      return (
                        <React.Fragment key={`team-${teamId}`}>
                          {/* Team Owner Row */}
                          <tr className="bg-blue-50 hover:bg-blue-100">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleTeamExpand(teamId)}
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                >
                                  {isExpanded ? '▼' : '▶'}
                                </button>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {owner.first_name} {owner.last_name}
                                    <span className="ml-2 text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded">
                                      Team: {owner.team_name}
                                    </span>
                                    {members.length > 0 && (
                                      <span className="ml-1 text-xs text-blue-600">
                                        ({members.length} member{members.length !== 1 ? 's' : ''})
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-500">{owner.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {(() => {
                                const badge = renderSubscriptionBadge(
                                  owner.subscription_status,
                                  owner.monitoring_status,
                                  owner.monitoring_plan_id,
                                  owner.tier_name
                                );
                                return (
                                  <span className={`px-2 py-1 text-xs rounded-full ${badge.className}`}>
                                    {badge.label}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-6 py-4">
                              <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                                Team Owner
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">{owner.project_count}</td>
                            <td className="px-6 py-4 text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                owner.activity_count > 100 ? 'bg-green-100 text-green-800' :
                                owner.activity_count > 20 ? 'bg-blue-100 text-blue-800' :
                                owner.activity_count > 0 ? 'bg-gray-100 text-gray-800' :
                                'bg-gray-50 text-gray-400'
                              }`}>
                                {owner.activity_count || 0}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {(() => {
                                const lastActive = owner.last_activity_at || owner.last_login_at;
                                if (!lastActive) return <span className="text-gray-400">Never</span>;
                                const lastDate = new Date(lastActive);
                                const now = new Date();
                                const diffMs = now.getTime() - lastDate.getTime();
                                const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                                const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                                const diffMins = Math.floor(diffMs / (1000 * 60));
                                let timeAgo;
                                if (diffMins < 60) timeAgo = `${diffMins}m ago`;
                                else if (diffHours < 24) timeAgo = `${diffHours}h ago`;
                                else if (diffDays < 7) timeAgo = `${diffDays}d ago`;
                                else timeAgo = lastDate.toLocaleDateString();
                                return <span title={lastDate.toLocaleString()}>{timeAgo}</span>;
                              })()}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {new Date(owner.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex gap-2 flex-wrap">
                                <button onClick={() => fetchUserDetails(owner.id)} className="text-blue-600 hover:text-blue-800 text-sm">View</button>
                                <button onClick={() => handleImpersonate(owner.id)} className="text-purple-600 hover:text-purple-800 text-sm">Login As</button>
                                <button onClick={() => handleResetPassword(owner.id, owner.email)} className="text-orange-600 hover:text-orange-800 text-sm">Reset PW</button>
                                <button
                                  onClick={() => handleToggleAdaAccess(owner.id, owner.ada_enabled)}
                                  className={`text-sm ${owner.ada_enabled ? 'text-green-600 hover:text-green-800' : 'text-gray-400 hover:text-gray-600'}`}
                                  title={owner.ada_enabled ? 'Ada access enabled — click to disable' : 'Ada access disabled — click to enable'}
                                >
                                  {owner.ada_enabled ? 'Ada: On' : 'Ada: Off'}
                                </button>
                                <button
                                  onClick={() => handleToggleSearchAccess(owner.id, !!owner.universe_access)}
                                  className={`text-sm ${owner.universe_access || owner.universe_access_via_team ? 'text-violet-600 hover:text-violet-800' : 'text-gray-400 hover:text-gray-600'}`}
                                  title={owner.universe_access ? 'Search access enabled — click to remove'
                                    : owner.universe_access_via_team ? 'Has Search through their plan (every plan includes it, trials too) or their team'
                                    : 'No Search access — click to give'}
                                >
                                  {owner.universe_access ? 'Search: On' : owner.universe_access_via_team ? 'Search: On (plan/team)' : 'Search: Off'}
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* Team Members (collapsed/expanded) */}
                          {isExpanded && members.map((member) => (
                            <tr key={member.id} className="bg-gray-50 hover:bg-gray-100">
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2 pl-8">
                                  <span className="text-gray-400">└</span>
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {member.first_name} {member.last_name}
                                    </div>
                                    <div className="text-sm text-gray-500">{member.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                                  Team Member
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  member.team_role === 'admin' ? 'bg-purple-100 text-purple-800' :
                                  member.team_role === 'member' ? 'bg-blue-100 text-blue-800' :
                                  member.team_role === 'viewer' ? 'bg-gray-100 text-gray-800' :
                                  'bg-gray-100 text-gray-500'
                                }`}>
                                  {member.team_role || 'member'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900">{member.project_count}</td>
                              <td className="px-6 py-4 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  member.activity_count > 100 ? 'bg-green-100 text-green-800' :
                                  member.activity_count > 20 ? 'bg-blue-100 text-blue-800' :
                                  member.activity_count > 0 ? 'bg-gray-100 text-gray-800' :
                                  'bg-gray-50 text-gray-400'
                                }`}>
                                  {member.activity_count || 0}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                {(() => {
                                  const lastActive = member.last_activity_at || member.last_login_at;
                                  if (!lastActive) return <span className="text-gray-400">Never</span>;
                                  const lastDate = new Date(lastActive);
                                  const now = new Date();
                                  const diffMs = now.getTime() - lastDate.getTime();
                                  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                                  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                                  const diffMins = Math.floor(diffMs / (1000 * 60));
                                  let timeAgo;
                                  if (diffMins < 60) timeAgo = `${diffMins}m ago`;
                                  else if (diffHours < 24) timeAgo = `${diffHours}h ago`;
                                  else if (diffDays < 7) timeAgo = `${diffDays}d ago`;
                                  else timeAgo = lastDate.toLocaleDateString();
                                  return <span title={lastDate.toLocaleString()}>{timeAgo}</span>;
                                })()}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                {new Date(member.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex gap-2 flex-wrap">
                                  <button onClick={() => fetchUserDetails(member.id)} className="text-blue-600 hover:text-blue-800 text-sm">View</button>
                                  <button onClick={() => handleImpersonate(member.id)} className="text-purple-600 hover:text-purple-800 text-sm">Login As</button>
                                  <button onClick={() => handleResetPassword(member.id, member.email)} className="text-orange-600 hover:text-orange-800 text-sm">Reset PW</button>
                                  <button
                                    onClick={() => handleToggleAdaAccess(member.id, member.ada_enabled)}
                                    className={`text-sm ${member.ada_enabled ? 'text-green-600 hover:text-green-800' : 'text-gray-400 hover:text-gray-600'}`}
                                    title={member.ada_enabled ? 'Ada access enabled — click to disable' : 'Ada access disabled — click to enable'}
                                  >
                                    {member.ada_enabled ? 'Ada: On' : 'Ada: Off'}
                                  </button>
                                  <button
                                    onClick={() => handleToggleSearchAccess(member.id, !!member.universe_access)}
                                    className={`text-sm ${member.universe_access || member.universe_access_via_team ? 'text-violet-600 hover:text-violet-800' : 'text-gray-400 hover:text-gray-600'}`}
                                    title={member.universe_access ? 'Search access enabled — click to remove'
                                      : member.universe_access_via_team ? 'Has Search through their plan (every plan includes it) or their team'
                                      : 'No Search access — click to give'}
                                  >
                                    {member.universe_access ? 'Search: On' : member.universe_access_via_team ? 'Search: On (plan/team)' : 'Search: Off'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}

                    {/* Render solo users */}
                    {organizedUsers.soloUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </td>
                        <td className="px-6 py-4">
                          {(() => {
                            const badge = renderSubscriptionBadge(
                              user.subscription_status,
                              user.monitoring_status,
                              user.monitoring_plan_id,
                              user.tier_name
                            );
                            return (
                              <span className={`px-2 py-1 text-xs rounded-full ${badge.className}`}>
                                {badge.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            user.role === 'admin'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {user.project_count}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            user.activity_count > 100 ? 'bg-green-100 text-green-800' :
                            user.activity_count > 20 ? 'bg-blue-100 text-blue-800' :
                            user.activity_count > 0 ? 'bg-gray-100 text-gray-800' :
                            'bg-gray-50 text-gray-400'
                          }`}>
                            {user.activity_count || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {(() => {
                            const lastActive = user.last_activity_at || user.last_login_at;
                            if (!lastActive) return <span className="text-gray-400">Never</span>;

                            const lastDate = new Date(lastActive);
                            const now = new Date();
                            const diffMs = now.getTime() - lastDate.getTime();
                            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                            const diffMins = Math.floor(diffMs / (1000 * 60));

                            let timeAgo;
                            if (diffMins < 60) timeAgo = `${diffMins}m ago`;
                            else if (diffHours < 24) timeAgo = `${diffHours}h ago`;
                            else if (diffDays < 7) timeAgo = `${diffDays}d ago`;
                            else timeAgo = lastDate.toLocaleDateString();

                            return <span title={lastDate.toLocaleString()}>{timeAgo}</span>;
                          })()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2 flex-wrap">
                            <button onClick={() => fetchUserDetails(user.id)} className="text-blue-600 hover:text-blue-800 text-sm">View</button>
                            <button onClick={() => handleImpersonate(user.id)} className="text-purple-600 hover:text-purple-800 text-sm">Login As</button>
                            <button onClick={() => handleResetPassword(user.id, user.email)} className="text-orange-600 hover:text-orange-800 text-sm">Reset PW</button>
                            <button
                              onClick={() => handleToggleAdaAccess(user.id, user.ada_enabled)}
                              className={`text-sm ${user.ada_enabled ? 'text-green-600 hover:text-green-800' : 'text-gray-400 hover:text-gray-600'}`}
                              title={user.ada_enabled ? 'Ada access enabled — click to disable' : 'Ada access disabled — click to enable'}
                            >
                              {user.ada_enabled ? 'Ada: On' : 'Ada: Off'}
                            </button>
                            <button
                              onClick={() => handleToggleSearchAccess(user.id, !!user.universe_access)}
                              className={`text-sm ${user.universe_access || user.universe_access_via_team ? 'text-violet-600 hover:text-violet-800' : 'text-gray-400 hover:text-gray-600'}`}
                              title={user.universe_access ? 'Search access enabled — click to remove'
                                : user.universe_access_via_team ? 'Has Search through their plan (every plan includes it, trials too) or their team'
                                : 'No Search access — click to give'}
                            >
                              {user.universe_access ? 'Search: On' : user.universe_access_via_team ? 'Search: On (plan/team)' : 'Search: Off'}
                            </button>
                            {!user.team_id && (
                              <button
                                onClick={() => {
                                  setEditingUserLogoId(user.id);
                                  setEditingUserLogoUrl(user.logo_url || '');
                                }}
                                className="text-yellow-600 hover:text-yellow-800 text-sm"
                              >
                                Logo
                              </button>
                            )}
                            <button
                              onClick={() => setDeleteUserConfirm({ show: true, userId: user.id, userEmail: user.email })}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Delete
                            </button>
                          </div>
                          {/* Inline logo editor for solo users */}
                          {editingUserLogoId === user.id && (
                            <div className="mt-2 flex gap-2">
                              <input
                                type="text"
                                value={editingUserLogoUrl}
                                onChange={(e) => setEditingUserLogoUrl(e.target.value)}
                                placeholder="Logo URL"
                                className="flex-1 px-2 py-1 text-xs border rounded"
                              />
                              <button
                                onClick={() => handleUpdateUserLogo(user.id, editingUserLogoUrl)}
                                className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingUserLogoId(null);
                                  setEditingUserLogoUrl('');
                                }}
                                className="px-2 py-1 text-xs bg-gray-300 rounded hover:bg-gray-400"
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* User Details Panel */}
          <div className="lg:col-span-1">
            {selectedUser ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">User Details</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-gray-500">Name</label>
                    <p className="font-medium">
                      {selectedUser.user.first_name} {selectedUser.user.last_name}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500">Email</label>
                    <p className="font-medium">{selectedUser.user.email}</p>
                  </div>

                  {selectedUser.user.company && (
                    <div>
                      <label className="text-xs text-gray-500">Company</label>
                      <p className="font-medium">{selectedUser.user.company}</p>
                    </div>
                  )}

                  <div>
                    <label className="text-xs text-gray-500">Role</label>
                    <select
                      value={selectedUser.user.role}
                      onChange={(e) => handleUpdateRole(selectedUser.user.id, e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500">Subscription Status</label>
                    <p className="font-medium">
                      {(() => {
                        const badge = renderSubscriptionBadge(
                          selectedUser.user.subscription_status,
                          selectedUser.user.monitoring_status,
                          selectedUser.user.monitoring_plan_id,
                          selectedUser.user.tier_name
                        );
                        return (
                          <span className={`px-2 py-1 text-xs rounded-full ${badge.className}`}>
                            {badge.label}
                          </span>
                        );
                      })()}
                    </p>
                  </div>

                  {selectedUser.user.stripe_customer_id && (
                    <div>
                      <label className="text-xs text-gray-500">Stripe Customer</label>
                      <p className="font-medium text-xs text-gray-600 font-mono">
                        {selectedUser.user.stripe_customer_id}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="text-xs text-gray-500">Email Verified</label>
                    <p className="font-medium">
                      {selectedUser.user.email_verified ? 'Yes' : 'No'}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500">Joined</label>
                    <p className="font-medium">
                      {new Date(selectedUser.user.created_at).toLocaleDateString()}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500">Last Login</label>
                    <p className="font-medium">
                      {selectedUser.user.last_login_at 
                        ? new Date(selectedUser.user.last_login_at).toLocaleDateString()
                        : 'Never'}
                    </p>
                  </div>

                 <div className="pt-4 border-t">
  <label className="text-xs text-gray-500 mb-2 block">
    Projects ({selectedUser.projects.length})
  </label>
  <div className="space-y-2">
    {selectedUser.projects.map((project) => (
      <div key={project.id} className="p-3 bg-gray-50 rounded flex justify-between items-start">
        <div className="flex-1">
          <p className="text-sm font-medium">{project.name}</p>
          <p className="text-xs text-gray-500">
            {new Date(project.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex gap-1 ml-2">
          <button
            onClick={() => setTransferModal({
              isOpen: true,
              projectId: project.id,
              projectName: project.name,
              fromUserId: selectedUser.user.id
            })}
            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Transfer
          </button>
          <button
            onClick={() => handleDuplicateProject(project.id, project.name, selectedUser.user.id)}
            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
          >
            Duplicate
          </button>
        </div>
      </div>
    ))}
                      {selectedUser.projects.length === 0 && (
                        <p className="text-sm text-gray-500">No projects</p>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => handleImpersonate(selectedUser.user.id)}
                    className="w-full mt-4 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                  >
                    Impersonate User
                  </button>

                  <button
                    onClick={() => setAddCreditsModal({
                      show: true,
                      userId: selectedUser.user.id,
                      userName: `${selectedUser.user.first_name} ${selectedUser.user.last_name}`,
                      userEmail: selectedUser.user.email
                    })}
                    className="w-full mt-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Add Credits
                  </button>

                  <button
                    onClick={() => setGiftBoltOnModal({
                      show: true,
                      userId: selectedUser.user.id,
                      userName: `${selectedUser.user.first_name} ${selectedUser.user.last_name}`,
                      userEmail: selectedUser.user.email
                    })}
                    className="w-full mt-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    Gift Bolt-on
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <p className="text-gray-500 text-center">
                  Select a user to view details
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Team Logo Management Section */}
        {teams.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold">Team Branding (White-Label)</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Set custom logos for teams to replace MarketMapr branding in their dashboard
                  </p>
                </div>
                <button
                  onClick={fetchTeams}
                  className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                >
                  Refresh
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {teams.map((team) => (
                  <div key={team.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-medium text-gray-900">{team.name}</h3>
                        <p className="text-xs text-gray-500">{team.member_count} member{team.member_count !== 1 ? 's' : ''}</p>
                      </div>
                      {team.logo_url && (
                        <div className="w-12 h-12 rounded border bg-white flex items-center justify-center overflow-hidden">
                          <img
                            src={team.logo_url}
                            alt={`${team.name} logo`}
                            className="max-w-full max-h-full object-contain"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {editingTeamId === team.id ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editingLogoUrl}
                          onChange={(e) => setEditingLogoUrl(e.target.value)}
                          placeholder="Enter logo URL (leave empty to remove)"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleUpdateTeamLogo(team.id, editingLogoUrl)}
                            className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingTeamId(null);
                              setEditingLogoUrl('');
                            }}
                            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {team.logo_url ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-green-600 flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Custom logo set
                            </span>
                            <button
                              onClick={() => {
                                setEditingTeamId(team.id);
                                setEditingLogoUrl(team.logo_url || '');
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm('Remove custom logo for this team?')) {
                                  handleUpdateTeamLogo(team.id, '');
                                }
                              }}
                              className="text-xs text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setEditingTeamId(team.id);
                              setEditingLogoUrl('');
                            }}
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            + Add custom logo
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {teams.length === 0 && (
                <p className="text-gray-500 text-center py-4">No teams found</p>
              )}
            </div>
          </div>
        )}

      </div>

{/* Transfer Project Modal */}
      {transferModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Transfer Project</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Project: <span className="font-medium">{transferModal.projectName}</span>
              </p>
              <p className="text-sm text-gray-600">
                Current Owner: <span className="font-medium">
                  {selectedUser?.user.first_name} {selectedUser?.user.last_name}
                </span>
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transfer to:
              </label>
              <input
                type="text"
                value={transferUserSearch}
                onChange={(e) => setTransferUserSearch(e.target.value)}
                placeholder="🔍 Search by name or email..."
                autoFocus
                className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-md text-sm"
              />
              {(() => {
                const q = transferUserSearch.trim().toLowerCase();
                const matches = users
                  .filter(u => u.id !== transferModal.fromUserId)
                  .filter(u => !q ||
                    `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase().includes(q) ||
                    (u.email || '').toLowerCase().includes(q));
                return (
                  <select
                    value={targetUserId}
                    onChange={(e) => setTargetUserId(e.target.value)}
                    size={Math.min(8, Math.max(2, matches.length + 1))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select a user...</option>
                    {matches.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </option>
                    ))}
                  </select>
                );
              })()}
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleTransferProject}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Transfer
              </button>
              <button
                onClick={() => {
                  setTransferModal({ isOpen: false, projectId: null, projectName: '', fromUserId: null });
                  setTargetUserId('');
                  setTransferUserSearch('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {deleteUserConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete User</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete <strong>{deleteUserConfirm.userEmail}</strong>?
              This will permanently delete the user and all their data including projects, candidates, and companies.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleDeleteUser}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete User
              </button>
              <button
                onClick={() => setDeleteUserConfirm({ show: false, userId: null, userEmail: '' })}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gift Bolt-on Modal */}
      {giftBoltOnModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Gift Bolt-on</h3>
            <p className="text-sm text-gray-600 mb-4">
              Add a free monitoring or jobs bolt-on for <strong>{giftBoltOnModal.userName}</strong> ({giftBoltOnModal.userEmail}).
              No Stripe charge — applied directly to their entitlements.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bolt-on type
                </label>
                <select
                  value={boltOnType}
                  onChange={(e) => setBoltOnType(e.target.value as 'monitoring' | 'jobs')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="monitoring">Monitoring (+5,000 checks/mo per unit)</option>
                  <option value="jobs">Jobs (+500 tracked jobs/mo per unit)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  min="1"
                  value={boltOnQuantity}
                  onChange={(e) => setBoltOnQuantity(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Total: {(parseInt(boltOnQuantity || '0', 10) * (boltOnType === 'monitoring' ? 2500 : 500)).toLocaleString()}{' '}
                  {boltOnType === 'monitoring' ? 'monitoring checks/mo' : 'tracked jobs/mo'}
                </p>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={handleGiftBoltOn}
                disabled={isGiftingBoltOn || !boltOnQuantity || parseInt(boltOnQuantity, 10) < 1}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGiftingBoltOn ? 'Gifting…' : 'Gift Bolt-on'}
              </button>
              <button
                onClick={() => {
                  setGiftBoltOnModal({ show: false, userId: null, userName: '', userEmail: '' });
                  setBoltOnQuantity('1');
                  setBoltOnType('monitoring');
                }}
                disabled={isGiftingBoltOn}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Remove Credits Modal */}
      {addCreditsModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Manage Credits</h3>
            <p className="text-sm text-gray-600 mb-4">
              Add or remove credits for <strong>{addCreditsModal.userName}</strong> ({addCreditsModal.userEmail})
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Credit Amount
                </label>
                <input
                  type="number"
                  value={creditsToAdd}
                  onChange={(e) => setCreditsToAdd(e.target.value)}
                  placeholder="e.g., 1000 or -500"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    creditsToAdd && parseInt(creditsToAdd) < 0
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-green-500'
                  }`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use positive numbers to add, negative numbers (e.g., -100) to remove
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason (optional)
                </label>
                <input
                  type="text"
                  value={creditReason}
                  onChange={(e) => setCreditReason(e.target.value)}
                  placeholder={creditsToAdd && parseInt(creditsToAdd) < 0
                    ? "e.g., Unused credits from enrichment"
                    : "e.g., Additional mapping work for client"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Quick amount buttons */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quick add
                </label>
                <div className="flex flex-wrap gap-2">
                  {[100, 500, 1000, 2500, 5000, 10000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setCreditsToAdd(amount.toString())}
                      className="px-3 py-1 text-sm border border-green-300 text-green-700 rounded-md hover:bg-green-50"
                    >
                      +{amount.toLocaleString()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick remove buttons */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quick remove
                </label>
                <div className="flex flex-wrap gap-2">
                  {[-100, -250, -500, -1000, -2500].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setCreditsToAdd(amount.toString())}
                      className="px-3 py-1 text-sm border border-red-300 text-red-700 rounded-md hover:bg-red-50"
                    >
                      {amount.toLocaleString()}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={handleAddCredits}
                disabled={isAddingCredits || !creditsToAdd}
                className={`flex-1 px-4 py-2 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                  creditsToAdd && parseInt(creditsToAdd) < 0
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {isAddingCredits
                  ? 'Processing...'
                  : creditsToAdd && parseInt(creditsToAdd) < 0
                    ? `Remove ${Math.abs(parseInt(creditsToAdd)).toLocaleString()} Credits`
                    : `Add ${creditsToAdd ? parseInt(creditsToAdd).toLocaleString() : 0} Credits`
                }
              </button>
              <button
                onClick={() => {
                  setAddCreditsModal({ show: false, userId: null, userName: '', userEmail: '' });
                  setCreditsToAdd('');
                  setCreditReason('');
                }}
                disabled={isAddingCredits}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Result Modal */}
      {resetPasswordResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Password Reset Successful</h3>
            <p className="text-sm text-gray-600 mb-4">
              New temporary password for <strong>{resetPasswordResult.email}</strong>:
            </p>

            <div className="bg-gray-100 p-4 rounded-md mb-4">
              <code className="text-lg font-mono text-gray-900 select-all">{resetPasswordResult.password}</code>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Share this password with the user. They should change it after logging in.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(resetPasswordResult.password);
                  alert('Password copied to clipboard!');
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Copy Password
              </button>
              <button
                onClick={() => setResetPasswordResult(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
